// ============================================================================
// SHARED SCOPE HTML RENDERER — renderScopeHtml.js
// ============================================================================
// Pure JavaScript — NO browser-only APIs (no window, no DOM, no document).
// Used client-side (ContractsScopePage) and server-side (api/scope-pdf.js).
//
// Export: buildScopeHtml(scopeData) → full HTML document string
//
// Margins: header is full-bleed (0). Content area: 1.5cm top (below header),
// 1.5cm bottom, 2cm left, 2cm right.
// Page numbers: bottom-right via CSS @page + counter.
// ============================================================================

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\u2014/g, ' - ')
    .replace(/\u2013/g, ' - ')
    .replace(/\u2018/g, "'")
    .replace(/\u2019/g, "'")
    .replace(/\u201C/g, '"')
    .replace(/\u201D/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ')
    .replace(/\uFFFD/g, '')
    .replace(/[\uFFF0-\uFFFF]/g, '');
}

function resolveTokens(text, deliverables) {
  if (!text || !deliverables) return text || '';
  const tokenMap = {};
  deliverables.forEach(d => {
    if (d.quantityToken && d.quantity) {
      tokenMap[d.quantityToken] = d.quantity;
    }
  });
  return text.replace(/\{\{([^}]+)\}\}/g, (match, token) => {
    const val = tokenMap[token.trim()];
    return val !== undefined ? val : match;
  });
}

function resolveNotesTokens(text, stage) {
  if (!text) return '';
  let resolved = text;
  if (stage.notesRevisions !== undefined) {
    resolved = resolved.replace(/\{\{revisions\}\}/g, stage.notesRevisions);
  }
  if (stage.notesMeetings !== undefined) {
    resolved = resolved.replace(/\{\{meetings\}\}/g, stage.notesMeetings);
  }
  return resolved;
}

function renderMarkup(text) {
  if (!text) return '';
  const lines = text.split('\n');
  return lines.map(line => {
    const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map(part => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return `<strong>${escapeHtml(part.slice(2, -2))}</strong>`;
      } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return `<em>${escapeHtml(part.slice(1, -1))}</em>`;
      }
      return escapeHtml(part);
    }).join('');
  }).join('<br/>');
}

// Default styles used when no custom styles are provided
const FALLBACK_STYLES = {
  body:    { fontFamily: 'Raleway', fontSize: 9 },
  heading: { fontFamily: 'Raleway', fontSize: 9.5 },
  title:   { fontFamily: 'Raleway', fontSize: 18 },
  notes:   { fontFamily: 'Raleway', fontSize: 8.5 },
};

const INTERIOR_SECTION_RENDER = [
  { key: 'warm-shell', label: 'Warm Shell' },
  { key: 'ffe', label: 'FF&E' },
];

export function buildScopeHtml(scopeData) {
  const {
    title,
    version,
    projectName,
    clientName,
    templateIntroduction,
    // Legacy single-toggle for older callers — treated as "both sections on".
    interiorDeliverablesSelected,
    interiorWarmShellSelected,
    interiorFfeSelected,
    interiorIntroduction,
    interiorDeliverables,
    stages,
    pdfStyles,
    headerImageUrl,
    officeName,
    paymentStages = [],
  } = scopeData;

  const s = {
    body:    { ...FALLBACK_STYLES.body,    ...(pdfStyles?.body || {}) },
    heading: { ...FALLBACK_STYLES.heading, ...(pdfStyles?.heading || {}) },
    title:   { ...FALLBACK_STYLES.title,   ...(pdfStyles?.title || {}) },
    notes:   { ...FALLBACK_STYLES.notes,   ...(pdfStyles?.notes || {}) },
  };

  const selectedStages = (stages || []).filter(st => st.selected);

  // Header HTML — full bleed, same as invoice
  const headerHtml = headerImageUrl
    ? `<div class="header-img"><img src="${headerImageUrl}" crossorigin="anonymous" /></div>`
    : `<div class="header-text">BRIO - ${escapeHtml(officeName || 'Architecture')}</div>`;

  // Interior Deliverables — split into Warm Shell / FF&E. Each section is
  // rendered only when its toggle is on AND it has at least one included
  // item. Falls back to the legacy single-toggle for older payloads.
  let interiorSection = '';
  if (interiorDeliverables) {
    const legacyOn = interiorWarmShellSelected === undefined && interiorFfeSelected === undefined
      ? !!interiorDeliverablesSelected
      : false;
    const sectionSelected = {
      'warm-shell': legacyOn || !!interiorWarmShellSelected,
      'ffe': legacyOn || !!interiorFfeSelected,
    };
    const renderedSections = INTERIOR_SECTION_RENDER
      .filter(s => sectionSelected[s.key])
      .map(s => {
        const items = interiorDeliverables.filter(d => (d.section || 'warm-shell') === s.key && d.included);
        return { ...s, items };
      })
      .filter(s => s.items.length > 0);
    const hasIntro = !!(interiorIntroduction && interiorIntroduction.trim());
    if (renderedSections.length > 0 || (hasIntro && (sectionSelected['warm-shell'] || sectionSelected['ffe']))) {
      const introHtml = hasIntro ? `<div class="intro">${renderMarkup(interiorIntroduction)}</div>` : '';
      const sectionsHtml = renderedSections.map(s => `
          <h3 class="interior-subheading">${escapeHtml(s.label)}</h3>
          <ol>${s.items.map(d => `<li>${escapeHtml(d.label)}</li>`).join('\n            ')}</ol>`).join('\n');
      interiorSection = `
        <div class="section">
          <h2>Summary of Interior Deliverables</h2>
          ${introHtml}
          ${sectionsHtml}
        </div>`;
    }
  }

  // Stage sections
  const stageSections = selectedStages.map(stage => {
    const allDelivs = [
      ...(stage.deliverables?.standard || []),
      ...(stage.deliverables?.optional || []),
    ];
    const included = [
      ...(stage.deliverables?.standard || []).filter(d => d.included),
      ...(stage.deliverables?.optional || []).filter(d => d.included),
      ...(stage.deliverables?.custom || []).filter(d => d.included),
    ];

    let introHtml = '';
    if (stage.introduction) {
      const resolved = resolveTokens(stage.introduction, allDelivs);
      introHtml = `<div class="intro">${renderMarkup(resolved)}</div>`;
    }

    let descHtml = '';
    if (stage.description) {
      descHtml = `<p class="description">${escapeHtml(stage.description)}</p>`;
    }

    let delivHtml = '';
    if (included.length > 0) {
      delivHtml = `
          <ol class="deliverables">
            ${included.map(d => {
              const label = resolveTokens(d.label, allDelivs);
              // Badge non-standard deliverables with their type, but suppress
              // "custom": "(optional)" is meaningful to the client, whereas
              // "(custom)" is an internal classification (imported / hand-added
              // items) and is just noise in the client-facing PDF.
              const typeBadge = d.type !== 'standard' && d.type !== 'custom'
                ? ` <span class="type">(${escapeHtml(d.type)})</span>`
                : '';
              return `<li>${escapeHtml(label)}${typeBadge}</li>`;
            }).join('\n            ')}
          </ol>`;
    }

    let notesHtml = '';
    if (stage.notes) {
      let resolved = resolveNotesTokens(stage.notes, stage);
      resolved = resolveTokens(resolved, allDelivs);
      notesHtml = `<div class="notes">${renderMarkup(resolved)}</div>`;
    }

    const fullName = stage.fullName && stage.fullName !== stage.name
      ? ` <span class="full-name">- ${escapeHtml(stage.fullName)}</span>`
      : '';

    return `
        <div class="stage">
          <h2>${escapeHtml(stage.name)}${fullName}</h2>
          ${descHtml}
          ${introHtml}
          ${delivHtml}
          ${notesHtml}
        </div>`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>${escapeHtml(title || 'Scope of Work')}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com"/>
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
  ${[...new Set([s.body.fontFamily, s.heading.fontFamily, s.title.fontFamily, s.notes.fontFamily])].map(f =>
    `<link href="https://fonts.googleapis.com/css2?family=${encodeURIComponent(f)}:wght@300;400;600;700&display=swap" rel="stylesheet"/>`
  ).join('\n  ')}
  <style>
* { box-sizing: border-box; margin: 0; padding: 0; }

@page {
  size: A4;
  margin-top: 25mm;
}
@page :first {
  margin-top: 0;
}

html, body {
  width: 210mm;
  min-height: 297mm;
  margin: 0;
  padding: 0;
}
body {
  font-family: '${escapeHtml(s.body.fontFamily)}', 'Century Gothic', system-ui, sans-serif;
  font-size: ${s.body.fontSize}pt;
  line-height: 1.6;
  color: #222;
  font-weight: 300;
  display: flex;
  flex-direction: column;
}

/* Header — full bleed on page 1 (Playwright top margin is 0) */
.header-img { margin: 0; padding: 0; flex-shrink: 0; }
.header-img img { width: 100%; display: block; }
.header-text {
  font-size: 18px; font-weight: 300; color: #9F902A;
  padding: 24px 2cm 12px;
  border-bottom: 1px solid #ddd;
  flex-shrink: 0;
}

/* Main content area */
.main-content {
  flex: 1;
  padding: 1cm 2cm 0 2cm;
}

h1 {
  font-family: '${escapeHtml(s.title.fontFamily)}', sans-serif;
  font-size: ${s.title.fontSize}pt;
  font-weight: 700;
  color: #9F902A;
  margin-bottom: 0.3cm;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
h1 .version-label {
  font-size: ${Math.max(s.title.fontSize - 8, 8)}pt;
  font-weight: 400;
  color: #999;
  letter-spacing: 0;
}
.subtitle {
  font-family: '${escapeHtml(s.notes.fontFamily)}', sans-serif;
  font-size: ${s.notes.fontSize}pt;
  color: #666;
  margin-bottom: 1cm;
}
h2 {
  font-family: '${escapeHtml(s.heading.fontFamily)}', sans-serif;
  font-size: ${s.heading.fontSize}pt;
  font-weight: 600;
  color: #222;
  margin-bottom: 0.4cm;
  padding-bottom: 0.15cm;
  border-bottom: 1px solid #9F902A;
  margin-top: 0.9cm;
  text-transform: uppercase;
}
h2 .full-name {
  font-weight: 400;
  color: #666;
  font-size: ${Math.max(s.heading.fontSize - 2, 8)}pt;
}
h3.interior-subheading {
  font-family: '${escapeHtml(s.heading.fontFamily)}', sans-serif;
  font-size: ${Math.max(s.heading.fontSize - 1, 10)}pt;
  font-weight: 600;
  color: #333;
  margin: 0.3cm 0 0.2cm;
}
.section { margin-bottom: 1cm; page-break-inside: avoid; }
.stage { margin-bottom: 1cm; page-break-inside: avoid; }
.intro {
  font-weight: 300;
  margin-bottom: 0.5cm;
  line-height: 1.7;
}
.description { color: #555; margin-bottom: 0.5cm; }
.template-intro {
  font-weight: 300;
  margin-bottom: 1cm;
  line-height: 1.7;
  color: #444;
  font-style: italic;
}
ol { padding-left: 1cm; margin-bottom: 0.5cm; }
ol li { margin-bottom: 0.1cm; }
ol.deliverables li { color: #444; }
.qty { color: #888; font-size: ${Math.max(s.body.fontSize - 2, 7)}pt; }
.type { color: #888; font-size: ${Math.max(s.body.fontSize - 3, 6)}pt; font-style: italic; }
.notes {
  font-family: '${escapeHtml(s.notes.fontFamily)}', sans-serif;
  font-size: ${s.notes.fontSize}pt;
  font-style: italic;
  color: #555;
  margin-top: 0.3cm;
  line-height: 1.6;
}

@media print {
  .stage { page-break-inside: avoid; }
}
table.fee-schedule {
  width: 100%;
  border-collapse: collapse;
  font-size: ${s.body.fontSize}pt;
  margin-top: 0.3cm;
}
table.fee-schedule th {
  font-weight: 600;
  border-bottom: 1px solid #9F902A;
  padding: 3px 6px;
  color: #222;
}
table.fee-schedule td {
  border-bottom: 1px solid #e5e7eb;
  padding: 3px 6px;
  color: #444;
}
  </style>
</head>
<body>

  ${headerHtml}

  <div class="main-content">
    <h1>${escapeHtml(title || 'Scope of Work')}${version ? ` <span class="version-label">v${version}</span>` : ''}</h1>
    ${projectName || clientName ? `<div class="subtitle">${[projectName, clientName].filter(Boolean).map(escapeHtml).join(' - ')}</div>` : ''}

    ${templateIntroduction ? `<div class="template-intro">${renderMarkup(templateIntroduction)}</div>` : ''}

    ${interiorSection}

    ${stageSections}

    ${(paymentStages || []).length > 0 ? `
    <div class="section">
      <h2>Fee Schedule</h2>
      <table class="fee-schedule">
        <thead>
          <tr>
            <th style="text-align:left;">Stage</th>
            <th style="text-align:right;width:80px;">Proportion</th>
            <th style="text-align:right;width:80px;">Cumulative</th>
          </tr>
        </thead>
        <tbody>
          ${(paymentStages || []).map(s => `
          <tr>
            <td>${escapeHtml(s.name || '')}</td>
            <td style="text-align:right;">${escapeHtml(s.proportion || '')}</td>
            <td style="text-align:right;">${escapeHtml(s.cumulative || '')}</td>
          </tr>`).join('')}
        </tbody>
      </table>
    </div>` : ''}
  </div>

</body>
</html>`;
}
